import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import moment from 'moment';
import { formatPhoneNumber } from '../../utils/phoneUtils';
import { Card, Button, Form, Modal } from 'react-bootstrap';
import { LuArrowLeft, LuTruck, LuPackage, LuCalendar, LuMapPin, LuUser, LuLink } from 'react-icons/lu';
import { getShipmentById, updateShipmentStatus, updateShipmentArrival } from '../../redux/reducers/shipmentSlice';
import ShipmentStatusBadge from './partials/ShipmentStatusBadge';
import ShipmentTimeline from './partials/ShipmentTimeline';

export default function ShipmentDetail() {
    const { id } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const shipment = useSelector(state => state.shipment.currentShipment);

    const [statusModal, setStatusModal] = useState(false);
    const [etaModal, setEtaModal] = useState(false);

    const [updateForm, setUpdateForm] = useState({ status: '', subStatus: '', note: '' });
    const [etaForm, setEtaForm] = useState({ newDate: '', delayReason: '' });

    useEffect(() => {
        if (id) dispatch(getShipmentById(id));
    }, [dispatch, id]);

    useEffect(() => {
        if (shipment) {
            setUpdateForm({
                status: shipment.status,
                subStatus: shipment.subStatus,
                note: ''
            });
            setEtaForm({
                newDate: shipment.estimatedArrival.current ? new Date(shipment.estimatedArrival.current).toISOString().split('T')[0] : '',
                delayReason: shipment.estimatedArrival.delayReason || ''
            });
        }
    }, [shipment]);

    const handleStatusUpdate = async (e) => {
        e.preventDefault();
        const res = await dispatch(updateShipmentStatus({ id, statusData: updateForm }));
        if (!res.error) setStatusModal(false);
    };

    const handleEtaUpdate = async (e) => {
        e.preventDefault();
        const res = await dispatch(updateShipmentArrival({ id, arrivalData: etaForm }));
        if (!res.error) setEtaModal(false);
    };

    if (!shipment) return <div className="text-center p-5">Loading shipment...</div>;

    const { deliveryType, selfDelivery, thirdParty, deliveryAddress, items, estimatedArrival } = shipment;

    const selfStatuses = ['AWAITING_PACKING', 'PACKED', 'DRIVER_ASSIGNED', 'PICKED_UP', 'AT_DOOR', 'DELIVERED', 'DELIVERY_FAILED', 'CANCELLED'];
    const thirdStatuses = ['LABEL_CREATED', 'HANDED_TO_CARRIER', 'IN_TRANSIT', 'CUSTOMS_CLEARANCE', 'DELAYED', 'ATTEMPTED_DELIVERY', 'DELIVERED', 'RETURNED_TO_SENDER', 'CANCELLED'];

    const allowedSubStatuses = deliveryType === 'self' ? selfStatuses : thirdStatuses;

    return (
        <div className="container-fluid my-4">
            <div className="d-flex align-items-center gap-3 mb-4">
                <button className="btn btn-light rounded-circle p-2" onClick={() => navigate("/shipments")}>
                    <LuArrowLeft size={16} />
                </button>
                <div>
                    <h5 className="fw-bold text-dark mb-0">Shipment {shipment.trackingCode}</h5>
                    <div className="text-muted" style={{ fontSize: "11px" }}>
                        ID: {shipment._id}
                    </div>
                </div>
                <div className="ms-auto"><ShipmentStatusBadge status={shipment.status} /></div>
            </div>

            <div className="row">
                {/* LEFT SIDE */}
                <div className="col-12 col-md-8">
                    {/* Header Card */}
                    <Card className="border-0 shadow-sm rounded-4 mb-4" style={{ padding: "24px" }}>
                        <div className="d-flex align-items-start justify-content-between mb-4">
                            <div>
                                <div className="fw-bold text-dark d-flex align-items-center gap-2" style={{ fontSize: "20px" }}>
                                    {deliveryType === 'self' ? <LuTruck color="#28B0C4" /> : <LuPackage color="#28B0C4" />}
                                    {deliveryType === 'self' ? 'Self Delivery' : 'Third Party Carrier'}
                                </div>
                                <div className="text-muted mt-2" style={{ fontSize: "14px" }}>
                                    {deliveryType === 'self'
                                        ? `Driver: ${selfDelivery?.driverName || 'Unassigned'} ${selfDelivery?.driverPhone ? `(${formatPhoneNumber(selfDelivery.driverPhone)})` : ''}`
                                        : `Carrier: ${thirdParty?.carrierName} — Tracking: ${thirdParty?.trackingNumber || 'Not provided'}`}
                                </div>
                            </div>
                        </div>

                        {deliveryType === 'third_party' && thirdParty?.carrierTrackingUrl && (
                            <a href={thirdParty.carrierTrackingUrl} target="_blank" rel="noreferrer" 
                               className="btn rounded-pill fw-semibold mb-4 d-inline-flex align-items-center gap-2"
                               style={{ background: "#f8fafc", color: "#28B0C4", border: "1.5px solid #e2e8f0", fontSize: '13px' }}>
                                <LuLink size={14} /> Track securely via {thirdParty.carrierName}
                            </a>
                        )}

                        <div className="bg-light p-3 rounded-4 mb-0">
                            <h6 className="fw-bold text-dark mb-3" style={{ fontSize: "14px" }}>Shipped Items</h6>
                            <div className="table-responsive">
                                <table className="table table-sm table-borderless mb-0 align-middle">
                                    <tbody>
                                        {items?.map((item, idx) => (
                                            <tr key={idx} className="border-bottom">
                                                <td className="fw-semibold text-dark py-2">{item.productName}</td>
                                                <td className="text-end fw-bold text-muted py-2">× {item.quantity}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </Card>

                    {/* Delivery Address */}
                    <Card className="border-0 shadow-sm rounded-4 mb-4" style={{ padding: "24px" }}>
                        <div className="d-flex align-items-center gap-2 mb-3">
                            <div className="rounded-2" style={{ width: 4, height: 18, background: "#28B0C4" }} />
                            <h6 className="fw-bold text-dark mb-0" style={{ fontSize: "14px" }}>Destination</h6>
                        </div>
                        <div className="rounded-4 p-3 bg-light border">
                            <div className="d-flex align-items-start gap-3">
                                <div className="p-2 rounded-circle bg-white shadow-sm text-primary">
                                    <LuMapPin size={18} color="#28B0C4" />
                                </div>
                                <div className="text-muted" style={{ fontSize: "13px", lineHeight: '1.8' }}>
                                    <strong className="text-dark d-block mb-1">{deliveryAddress?.recipientName}</strong>
                                    {deliveryAddress?.street}<br />
                                    {deliveryAddress?.city}, {deliveryAddress?.state} {deliveryAddress?.zipCode}<br />
                                    {deliveryAddress?.country}
                                    {deliveryAddress?.phone && <div className="mt-1 d-flex align-items-center gap-2"><LuUser size={12}/> {formatPhoneNumber(deliveryAddress.phone)}</div>}
                                </div>
                            </div>
                        </div>
                        {shipment.specialInstructions && (
                            <div className="mt-3 p-3 rounded-4" style={{ background: '#fffbeb', border: '1px solid #fef3c7', color: '#b45309', fontSize: '13px' }}>
                                <strong>Instructions:</strong> {shipment.specialInstructions}
                            </div>
                        )}
                    </Card>

                    {/* Timeline Tracker */}
                    <Card className="border-0 shadow-sm rounded-4" style={{ padding: "24px" }}>
                        <div className="d-flex align-items-center gap-2 mb-4">
                            <div className="rounded-2" style={{ width: 4, height: 18, background: "#28B0C4" }} />
                            <h6 className="fw-bold text-dark mb-0" style={{ fontSize: "14px" }}>Timeline Activity</h6>
                        </div>
                        <ShipmentTimeline timeline={shipment.timeline} />
                    </Card>
                </div>

                {/* RIGHT SIDEBAR */}
                <div className="col-12 col-md-4 mt-4 mt-md-0">
                    
                    {/* Status Updator */}
                    <Card className="border-0 shadow-sm rounded-4 mb-4" style={{ padding: "20px" }}>
                        <div className="d-flex align-items-center gap-2 mb-3">
                            <div className="rounded-2" style={{ width: 4, height: 18, background: "#10b981" }} />
                            <h6 className="fw-bold text-dark mb-0" style={{ fontSize: "14px" }}>Manage Status</h6>
                        </div>
                        <Button variant="primary" className="w-100 rounded-pill fw-bold" onClick={() => setStatusModal(true)}>
                            Update Status
                        </Button>
                    </Card>

                    {/* ETA Card */}
                    <Card className="border-0 shadow-sm rounded-4 mb-4" style={{ padding: "20px" }}>
                        <div className="d-flex align-items-center gap-2 mb-3">
                            <div className="rounded-2" style={{ width: 4, height: 18, background: "#f59e0b" }} />
                            <h6 className="fw-bold text-dark mb-0" style={{ fontSize: "14px" }}>Estimated Arrival</h6>
                        </div>
                        
                        <div className="d-flex flex-column gap-2 mb-3">
                            <div className="px-3 py-2 rounded-3 text-center" style={{ background: estimatedArrival?.isDelayed ? '#fee2e2' : 'rgba(40,176,196,0.1)' }}>
                                <LuCalendar className={estimatedArrival?.isDelayed ? "text-danger" : "text-primary"} size={16} />
                                <h5 className={`mt-2 mb-0 fw-bold ${estimatedArrival?.isDelayed ? 'text-danger' : 'text-primary'}`}>
                                    {moment(estimatedArrival?.current).format('MMMM D, YYYY')}
                                </h5>
                                {estimatedArrival?.isDelayed && (
                                    <div className="small fw-semibold text-danger mt-1">Delayed</div>
                                )}
                            </div>
                        </div>
                        <Button variant="outline-secondary" size="sm" className="w-100 rounded-pill fw-bold" onClick={() => setEtaModal(true)}>
                            Modify ETA
                        </Button>
                    </Card>

                    {/* Meta Links */}
                    <Card className="border-0 shadow-sm rounded-4 mb-4" style={{ padding: "20px" }}>
                        <div className="d-flex align-items-center gap-2 mb-3">
                            <div className="rounded-2" style={{ width: 4, height: 18, background: "#64748b" }} />
                            <h6 className="fw-bold text-dark mb-0" style={{ fontSize: "14px" }}>References</h6>
                        </div>
                        
                        <div className="d-flex flex-column gap-2">
                            <div className="p-3 rounded-4 bg-light text-center border cursor-pointer" onClick={() => navigate(`/view-invoice/${shipment?.invoiceId?._id}`)} style={{ cursor: 'pointer' }}>
                                <div className="text-muted text-uppercase fw-bold" style={{ fontSize: '10px', letterSpacing: '0.5px' }}>Linked Invoice</div>
                                <div className="fw-semibold text-dark">#{shipment?.invoiceId?._id?.slice(-8).toUpperCase()}</div>
                            </div>
                        </div>
                    </Card>

                </div>
            </div>

            {/* STATUS MODAL */}
            <Modal show={statusModal} onHide={() => setStatusModal(false)} centered>
                <Form onSubmit={handleStatusUpdate}>
                    <Modal.Header closeButton className="border-0">
                        <Modal.Title className="fw-bold text-dark" style={{ fontSize: '18px' }}>Update Status</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Form.Group className="mb-3">
                            <Form.Label className="small fw-semibold text-muted text-uppercase">High Level Status</Form.Label>
                            <Form.Select value={updateForm.status} onChange={e => setUpdateForm({...updateForm, status: e.target.value})}>
                                <option value="PENDING">Pending</option>
                                <option value="PROCESSING">Processing</option>
                                <option value="DISPATCHED">Dispatched</option>
                                <option value="OUT_FOR_DELIVERY">Out For Delivery</option>
                                <option value="DELIVERED">Delivered</option>
                                <option value="FAILED">Failed</option>
                                <option value="RETURNED">Returned</option>
                                <option value="CANCELLED">Cancelled</option>
                            </Form.Select>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label className="small fw-semibold text-muted text-uppercase">Detailed Sub-status</Form.Label>
                            <Form.Select value={updateForm.subStatus} onChange={e => setUpdateForm({...updateForm, subStatus: e.target.value})}>
                                {allowedSubStatuses.map(ss => (
                                    <option key={ss} value={ss}>{ss.replace(/_/g, ' ')}</option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label className="small fw-semibold text-muted text-uppercase">Activity Note</Form.Label>
                            <Form.Control as="textarea" rows={2} required 
                                value={updateForm.note} 
                                onChange={e => setUpdateForm({...updateForm, note: e.target.value})} 
                                placeholder="Explain the context..." />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer className="border-0">
                        <Button variant="light" onClick={() => setStatusModal(false)} className="rounded-pill px-4 fw-bold">Cancel</Button>
                        <Button type="submit" variant="primary" className="rounded-pill px-4 fw-bold">Save Status</Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* ETA MODAL */}
            <Modal show={etaModal} onHide={() => setEtaModal(false)} centered>
                <Form onSubmit={handleEtaUpdate}>
                    <Modal.Header closeButton className="border-0">
                        <Modal.Title className="fw-bold text-dark" style={{ fontSize: '18px' }}>Modify Estimated Arrival</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <div className="alert alert-warning small px-3 py-2 border-0 rounded-4">
                            Original estimation was: <strong>{moment(estimatedArrival?.original).format('MMM D, YYYY')}</strong>. 
                            Modifying this date will flag the shipment as delayed if pushed backwards.
                        </div>
                        <Form.Group className="mb-3">
                            <Form.Label className="small fw-semibold text-muted text-uppercase">New Estimated Date</Form.Label>
                            <Form.Control type="date" required 
                                value={etaForm.newDate} 
                                onChange={e => setEtaForm({...etaForm, newDate: e.target.value})} />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label className="small fw-semibold text-muted text-uppercase">Reason for modification</Form.Label>
                            <Form.Control as="textarea" rows={2} required 
                                value={etaForm.delayReason} 
                                onChange={e => setEtaForm({...etaForm, delayReason: e.target.value})} 
                                placeholder="Weather delay..." />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer className="border-0">
                        <Button variant="light" onClick={() => setEtaModal(false)} className="rounded-pill px-4 fw-bold">Cancel</Button>
                        <Button type="submit" variant="primary" className="rounded-pill px-4 fw-bold">Update ETA</Button>
                    </Modal.Footer>
                </Form>
            </Modal>

        </div>
    );
}
